import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import termsHistoryStyles from "./termsHistoryStyles";
import styles from "./TermsAndConditionsHistory.module.scss";
import ClearIcon from "@mui/icons-material/Clear";
import { withTranslation } from "react-i18next";
import ConfirmDialog from "../../../shared/confirmDialog/ConfirmDialog";
import HasPermissionTo from "../../../common/HasPermissionTo";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import MotionChevronRight from "../../../shared/Icons/MotionChevronRight";
import EMPEmptyState from "../../../shared/components/EMPEmptyState/EMPEmptyState";

const TermsAndConditionsHistory = (props) => {
  const [openDialog, setOpenDialog] = useState(false);
  const selectedVersionRef = useRef(null);

  const deleteTermsDialogProps = useMemo(
    () => ({
      title: props.t("TermsandConditionsHistory.deleteTitle"),
      bodyText: props.t("TermsandConditionsHistory.deleteBody"),
      confirm: props.t("TermsandConditionsHistory.confirm"),
      cancel: props.t("TermsandConditionsHistory.cancel"),
    }),
    [props.t]
  );

  const handleClose = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleConfirm = useCallback(() => {
    props.deleteTerms(selectedVersionRef.current);
    handleClose();
  }, [handleClose, props]);

  const deleteTermsDialog = useCallback((version) => {
    selectedVersionRef.current = version;
    setOpenDialog(true);
  }, []);

  const loadTableCellHeader = useCallback(
    (style, divStyle, column, displayColumn) => (
      <TableCell style={style}>
        {displayColumn && (
          <div className={divStyle} id="header">
            {props.t(`TermsandConditionsHistory.${column}`)}
          </div>
        )}
      </TableCell>
    ),
    [props.t]
  );

  const showDeleteIcon = useCallback(
    (index) =>
      props.termsAndConditionsHistory.length > 1 &&
      index === 0 &&
      props.termsAndConditionsHistory[0].canBeDeleted,
    [props.termsAndConditionsHistory]
  );

  return (
    <div className={styles.title}>
      <h2 className="pagePaddingLeft" id="termsHeader">
        {props.t("TermsandConditionsHistory.title")}
      </h2>
      <Table id="termsHistory">
        <TableHead>
          <TableRow
            style={termsHistoryStyles.tableRow}
            className={styles.tableHeader}
          >
            {loadTableCellHeader(
              termsHistoryStyles.documentHeader,
              styles.alignStart,
              "document",
              true
            )}
            {props.isDealer &&
              loadTableCellHeader(
                termsHistoryStyles.effectiveDateHeader,
                styles.alignCenter,
                "acceptedOn",
                true
              )}
            {loadTableCellHeader(
              termsHistoryStyles.effectiveDateHeader,
              styles.alignCenter,
              "effectiveFrom",
              true
            )}
            {loadTableCellHeader(
              termsHistoryStyles.downloadHeader,
              null,
              "download",
              false
            )}
            {!props.isDealer && loadTableCellHeader(null, null, null, false)}
          </TableRow>
        </TableHead>

        <ConfirmDialog
          open={openDialog}
          dialogProps={deleteTermsDialogProps}
          onConfirm={handleConfirm}
          onCancel={handleClose}
          onClose={handleClose}
          boldTitle={false}
          {...props}
        />

        <TableBody className={styles.tableBody}>
          {props.termsAndConditionsHistory.map((termsAndConditions, index) => (
            <TableRow
              className="pagePaddingLeft pagePaddingRight"
              id={`row_${index}`}
              key={termsAndConditions.version}
            >
              <TableCell style={termsHistoryStyles.document}>
                {(props.user?.market || "").toUpperCase() +
                  " " +
                  props.t("TermsandConditionsHistory.documentName") +
                  termsAndConditions.version}
              </TableCell>
              {props.isDealer && (
                <TableCell style={termsHistoryStyles.effectiveDate}>
                  {termsAndConditions.accepted ? (
                    termsAndConditions.dealerApprovedDate
                  ) : (
                    <EMPEmptyState />
                  )}
                </TableCell>
              )}
              <TableCell style={termsHistoryStyles.effectiveDate}>
                {termsAndConditions.effectiveDate}
              </TableCell>
              <TableCell style={termsHistoryStyles.download}>
                <div>
                  <span>
                    {termsAndConditions.accepted ? (
                      <EMPButton
                        testId={`acceptLink_${index}`}
                        onClick={() =>
                          termsAndConditions.accepted &&
                          props.handleTermsAndConditionsDownload(
                            termsAndConditions
                          )
                        }
                        variant="text"
                        leftIcon={<CloudDownloadIcon />}
                      >
                        {props.t("TermsandConditionsHistory.download")}
                      </EMPButton>
                    ) : (
                      props.isDealer && (
                        <EMPButton
                          testId={`acceptLink_${index}`}
                          onClick={() =>
                            props.alterComponentView(termsAndConditions)
                          }
                          variant="text"
                          rightIcon={<MotionChevronRight />}
                        >
                          {props.t("TermsandConditionsHistory.review")}
                        </EMPButton>
                      )
                    )}
                  </span>
                </div>
              </TableCell>
              {!props.isDealer && (
                <TableCell style={termsHistoryStyles.rightCell}>
                  <div className={styles.deleteWrapper}>
                    <EMPButton
                      testId={`acceptLink_${index}`}
                      onClick={() =>
                        props.handleTermsAndConditionsDownload(termsAndConditions)
                      }
                      variant="text"
                      leftIcon={<CloudDownloadIcon />}
                    >
                      {props.t("TermsandConditionsHistory.download")}
                    </EMPButton>
                    <HasPermissionTo
                      permissions={props.user?.permissions?.rolePermissions}
                      perform={["deleteTermsConditions"]}
                      condition={showDeleteIcon(index)}
                      render={() => (
                        <Button
                          style={termsHistoryStyles.button}
                          data-testid={`delete_${index}`}
                          onClick={() =>
                            deleteTermsDialog(termsAndConditions.version)
                          }
                        >
                          <ClearIcon />
                        </Button>
                      )}
                    />
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

TermsAndConditionsHistory.propTypes = {
  t: PropTypes.func.isRequired,
  isDealer: PropTypes.bool,
  deleteTerms: PropTypes.func,
  alterComponentView: PropTypes.func,
  handleTermsAndConditionsDownload: PropTypes.func.isRequired,
  termsAndConditionsHistory: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      canBeDeleted: PropTypes.bool,
      accepted: PropTypes.bool,
      dealerApprovedDate: PropTypes.string,
      effectiveDate: PropTypes.string,
    })
  ).isRequired,
  user: PropTypes.shape({
    market: PropTypes.string,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.object,
      ]),
    }),
  }),
};

export default withTranslation("emp")(TermsAndConditionsHistory);
